'use client';

import { Box, Container, Grid, Paper, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

import { ServicesList } from '../../../services/types';

interface ServicesListSectionProps {
  data?: ServicesList;
}

export default function ServicesListSection({
  data,
}: ServicesListSectionProps) {
  const [activeService, setActiveService] = useState<number | null>(null);

  // Find the first active service or default to the first one
  useEffect(() => {
    if (data?.services && data.services.length > 0) {
      const defaultActive =
        data.services.find(service => service.isActive) || data.services[0];
      setActiveService(defaultActive.id);
    }
  }, [data?.services]);

  if (!data) {
    return null;
  }

  const activeServiceData = data.services?.find(
    service => service.id === activeService
  );

  return (
    <Box
      component='section'
      sx={{
        py: { xs: 6, md: 10 },
        backgroundColor: '#F7F9FC',
      }}
    >
      <Container maxWidth='lg'>
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant='h2'
            component='h2'
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: { xs: '28px', md: '36px' },
              lineHeight: 1.53,
              color: '#001A44',
              mb: 3,
              textTransform: 'uppercase',
            }}
          >
            {data.title}
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Services List */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {data.services && data.services.length > 0 ? (
                data.services.map((service, _index) => (
                  <Paper
                    key={service.id}
                    elevation={0}
                    onClick={() => setActiveService(service.id)}
                    sx={{
                      p: 3,
                      cursor: 'pointer',
                      border:
                        activeService === service.id
                          ? 'none'
                          : '1px solid #EB722E',
                      backgroundColor:
                        activeService === service.id
                          ? '#EB722E'
                          : 'transparent',
                      borderRadius: 0,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor:
                          activeService === service.id ? '#EB722E' : 'white',
                        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                      },
                    }}
                  >
                    <Typography
                      variant='h6'
                      component='h3'
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500,
                        fontSize: '20px',
                        lineHeight: 1.5,
                        color: activeService === service.id ? 'white' : '#000',
                        mb: 1.5,
                        textAlign: 'center',
                      }}
                    >
                      {service.title}
                    </Typography>

                    <Typography
                      variant='body2'
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 400,
                        fontSize: '16px',
                        lineHeight: 1.75,
                        color: activeService === service.id ? 'white' : '#000',
                        textAlign: 'center',
                      }}
                    >
                      {service.description}
                    </Typography>
                  </Paper>
                ))
              ) : (
                <Typography
                  variant='body1'
                  sx={{
                    textAlign: 'center',
                    color: '#6c757d',
                    fontStyle: 'italic',
                  }}
                >
                  Không có dịch vụ nào
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Service Details */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                backgroundColor: 'white',
                height: 'fit-content',
                borderRadius: 0,
              }}
            >
              {activeServiceData ? (
                <>
                  <Typography
                    variant='h5'
                    component='h3'
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 500,
                      fontSize: '20px',
                      lineHeight: 1.5,
                      color: '#231F20',
                      mb: 3,
                    }}
                  >
                    {data.overviewTitle || 'Overview'}
                  </Typography>

                  {activeServiceData.overview && (
                    <Typography
                      variant='body1'
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 400,
                        fontSize: '16px',
                        lineHeight: 1.75,
                        color: '#6E6F72',
                        mb: 3,
                        textAlign: 'justify',
                      }}
                    >
                      {activeServiceData.overview}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography
                  variant='body1'
                  sx={{
                    textAlign: 'center',
                    color: '#6c757d',
                    fontStyle: 'italic',
                  }}
                >
                  Chọn một dịch vụ để xem chi tiết
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
